import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Ruler, Sparkles, Check } from 'lucide-react';

export default function SizeGuidePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50/50 to-white py-12 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-sky-500/5 blur-[120px] pointer-events-none rounded-full"></div>

      <div className="max-w-4xl mx-auto space-y-6 relative z-10">
        <div>
          <button
            onClick={() => navigate("/")}
            className="group inline-flex items-center gap-2.5 text-xs font-bold text-slate-600 hover:text-sky-600 bg-white hover:bg-sky-50/50 border border-slate-200/80 px-4 py-2.5 rounded-2xl transition-all duration-200 shadow-sm hover:shadow cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> 
            <span>Quay lại</span>
          </button>
        </div>

        <div className="bg-white/95 backdrop-blur-xl border border-slate-200/80 rounded-3xl p-8 sm:p-12 space-y-8 shadow-2xl shadow-slate-200/50">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-sky-50 text-sky-600 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
                <Ruler className="w-8 h-8" />
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-sky-600 bg-sky-50 px-2.5 py-1 rounded-full">
                  Tiêu chuẩn Nhật Bản
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-serif mt-1.5">
                  Hướng Dẫn Chọn Size
                </h1>
              </div>
            </div>
            <div className="text-slate-400 text-xs flex items-center gap-1.5 self-start sm:self-center">
              <Sparkles className="w-3.5 h-3.5 text-sky-500" /> Bảng quy chuẩn chuẩn xác
            </div>
          </div>

          <div className="bg-gradient-to-r from-sky-50/60 to-slate-50 border border-sky-100/60 p-5 sm:p-6 rounded-2xl text-slate-700 leading-relaxed text-xs sm:text-sm font-medium">
            Form size hàng hóa Nhật Bản thường có sự khác biệt nhỏ so với form quốc tế. Vui lòng tham khảo kỹ bảng thông số chuẩn dưới đây trước khi đặt hàng tại <strong className="text-sky-600">JapanICHIBA</strong>.
          </div>

          <div className="space-y-8 text-xs sm:text-sm text-slate-600 leading-relaxed">
            
            {/* Bảng quy đổi áo quần */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                <span className="w-7 h-7 bg-slate-900 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">1</span>
                Bảng Quy Đổi Size Thời Trang Nam / Nữ
              </h3>
              
              <div className="overflow-x-auto border border-slate-200/80 rounded-2xl shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-900 text-white text-xs">
                      <th className="p-3.5 font-semibold">Size Chuẩn</th>
                      <th className="p-3.5 font-semibold">Chiều Cao (cm)</th>
                      <th className="p-3.5 font-semibold">Cân Nặng (kg)</th>
                      <th className="p-3.5 font-semibold">Vòng Ngực (cm)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600 text-xs">
                    <tr className="hover:bg-slate-50/80">
                      <td className="p-3.5 font-bold text-slate-900">S</td>
                      <td className="p-3.5">150 - 160</td>
                      <td className="p-3.5">45 - 53</td>
                      <td className="p-3.5">78 - 84</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80">
                      <td className="p-3.5 font-bold text-slate-900">M</td>
                      <td className="p-3.5">160 - 168</td>
                      <td className="p-3.5">53 - 62</td>
                      <td className="p-3.5">84 - 90</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80">
                      <td className="p-3.5 font-bold text-slate-900">L</td>
                      <td className="p-3.5">168 - 175</td>
                      <td className="p-3.5">62 - 72</td>
                      <td className="p-3.5">90 - 96</td>
                    </tr>
                    <tr className="hover:bg-slate-50/80">
                      <td className="p-3.5 font-bold text-slate-900">XL</td>
                      <td className="p-3.5">175 - 182</td>
                      <td className="p-3.5">72 - 82</td>
                      <td className="p-3.5">96 - 102</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mẹo đo */}
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2.5">
                <span className="w-7 h-7 bg-slate-900 text-white rounded-xl flex items-center justify-center text-xs shadow-sm">2</span>
                Mẹo Đo Thông Số Chính Xác
              </h3>
              <ul className="space-y-2 pl-2">
                <li className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Sử dụng thước dây mềm ôm sát vòng ngực, vòng eo và vòng mông qua phần lớn nhất.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>Nếu thông số của bạn nằm ở ranh giới giữa hai size, hãy ưu tiên chọn size lớn hơn để có trải nghiệm mặc thoải mái.</span>
                </li>
              </ul>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}